import { useCallback, lazy, Suspense, useState } from 'react';
import { object, func, bool, string } from 'prop-types';
import { useIntl } from '@alltrails/shared/react-intl';
import { defineMessages } from '@alltrails/shared/react-intl';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import StarRating from '@alltrails/shared/denali/components/StarRating';
import StarRatingInteractive from '../../shared/StarRating';
import BookmarkButton from '@alltrails/shared/components/BookmarkButton';
import { useLocalizedText } from '@alltrails/shared/modules/Localization';
import { wrapEventHandler } from 'utils/handlers';
import useToggle from '@alltrails/shared/hooks/useToggle';
import { monthDayYear } from '../../../utils/time_helpers';
import { buildMapMenuItems, buildTrackMenuItems } from '../../../utils/action_header_helper';
import { wrapUrlSafe } from '../../../utils/language_support_util';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { getTrackProfilePhotoUrl } from '../../../utils/track_helpers';
import { getImgDefaultStyle } from '../../cards/CardImage/CardImage';
import OriginalLanguageButton from '../OriginalLanguageButton';
import HeaderButtons from '../HeaderButtons';
import * as styles from './styles/styles.module.scss';
import CustomProvider from 'components/CustomProvider';
import AggRatingStructuredData, { aggRatingDataAttributes } from '../AggRatingStructuredData';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import Button from '@alltrails/shared/denali/components/Button';

const ReviewFormModal = lazy(() => import('components/forms/ReviewFormModal'));

const messages = defineMessages({
  ADD_A_TRAIL_REVIEW: { defaultMessage: 'Add a trail review' },
  EDIT_REVIEW: {
    defaultMessage: 'Edit review'
  }
});

const ActionHeader = ({
  data,
  isFavorite,
  handleFavoriteClick,
  bannerPhotoId,
  handleDirectionsClick,
  openDownloadModal,
  handleShareClick,
  handleDeleteClick,
  saveMapAsClone,
  type,
  editingEndpoint,
  handleEditEndpointClick,
  handleCancelEditEndpointClick,
  handleEditClick,
  excludeRatingStructuredData,
  handleRatingChange,
  rating,
  trackTrail,
  canEdit
}) => {
  const {
    formattedDefaultMessages: { ADD_A_TRAIL_REVIEW, EDIT_REVIEW }
  } = useFormatMessage(messages);

  const user = useUser();
  const intl = useIntl();
  let refreshMap;
  let updateHidden;

  const [refreshed, toggleRefreshed] = useToggle(false);
  const [hidden, toggleHidden] = useToggle(data?.hidden);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const id = type === 'map' ? data.id : data.ID;
  const descriptionSource = data?.description_source || data?.description;
  const languageRegionCode = useLanguageRegionCode();
  const {
    canBeLocalized,
    toggleText,
    isLocalized,
    text: localizedText
  } = useLocalizedText(descriptionSource, data?.description_lang, languageRegionCode, {
    table: 'activities',
    column: 'description',
    row: id
  });
  const [comment, setComment] = useState(data?.comment);

  const onReviewAdd = data => {
    setComment(data.detail?.comment);
  }
  const updateReview = data => {
    setComment(data.detail?.comment);
  };

  if (user?.admin) {
    refreshMap = () => {
      ServerCommunicationUtil.getApiEndpoint(
        `/api/alltrails/maps/${id}/refresh_map_cache`,
        {},
        () => toggleRefreshed(true),
        err => console.log(err),
        () => {}
      );
    };

    updateHidden = () => {
      ServerCommunicationUtil.postApiEndpoint(
        `/api/alltrails/maps/${data.id}/metadata`,
        { hidden: !hidden },
        () => toggleHidden(!hidden),
        () => {},
        () => {}
      );
    };
  }

  const favoriteClick = () => {
    handleFavoriteClick(data.presentationType, id);
  };

  const addAsNewMap = () => {
    window.open(wrapUrlSafe(`/trail/new?recording=${data.id}`, languageRegionCode), '_blank');
  };

  const getBannerImgStyle = useCallback(() => {
    const mapPhotoUrl = bannerPhotoId
      ? `/api/alltrails/photos/${bannerPhotoId}/image?size=large&key=${ServerCommunicationUtil.apiKey}`
      : getTrackProfilePhotoUrl(data);
    const backgroundImage = `url(${mapPhotoUrl})`;
    return getImgDefaultStyle(data.name, backgroundImage);
  }, [data, bannerPhotoId]);

  const isMapOwner = user?.id === data.user?.id;

  const getMenuItems = () => {
    if (type === 'map') {
      return buildMapMenuItems(
        data,
        openDownloadModal,
        refreshed,
        refreshMap,
        hidden,
        updateHidden,
        handleDeleteClick,
        addAsNewMap,
        isMapOwner,
        saveMapAsClone,
        intl,
        user
      );
    }
    if (type === 'track') {
      return buildTrackMenuItems(
        data,
        openDownloadModal,
        editingEndpoint,
        handleEditEndpointClick,
        handleCancelEditEndpointClick,
        handleEditClick,
        refreshed,
        refreshMap,
        handleDeleteClick,
        saveMapAsClone,
        intl,
        user
      );
    }
    return null;
  };

  const includeRatingStructuredData = !excludeRatingStructuredData && data.rating !== null;

  const activityToUpperCase = name => name.charAt(0).toUpperCase() + name.slice(1);

  return (
    <CustomProvider>
      <div className={styles.container}>
        <div className={styles.banner} style={getBannerImgStyle()}>
          <BookmarkButton className={styles.bookmarkButton} isSelected={isFavorite} onClick={wrapEventHandler(favoriteClick)} />
        </div>
        <HeaderButtons
          user={data?.user}
          handleDirectionsClick={handleDirectionsClick}
          handleShareClick={handleShareClick}
          menuItems={getMenuItems()}
        />
        <div className={styles.metadata}>
          <div className={styles.dateAndActivity}>
            {monthDayYear(data.created_at, languageRegionCode)} {data.activity?.name ? `${'\u2022'} ${activityToUpperCase(data?.activity.name)}` : ''}
          </div>
          {type === 'map' ? (
            <div className={styles.nameAndDescription}>
              <div className={styles.name}>{data.name}</div>
              <div className={styles.description}>{localizedText}</div>
              {canBeLocalized && <OriginalLanguageButton active={isLocalized} handleClick={toggleText} eventSource="Map Description" />}
            </div>
          ) : (
            <div className={styles.nameAndStars}>
              <div className={styles.trackName}>{data.name}</div>
              <span className={styles.starsBlock} {...(includeRatingStructuredData ? aggRatingDataAttributes : {})}>
                {canEdit ? (
                  <StarRatingInteractive config={rating} handler={handleRatingChange} iconClassName={styles.starRatingIconClass} />
                ) : (
                  <StarRating rating={rating} variant="complex" size="lg" className={styles.starRatingIconClass} />
                )}
                {includeRatingStructuredData && <AggRatingStructuredData rating={data.rating} />}
              </span>
              {canEdit && trackTrail ? (
                comment ? (
                  <div>
                    <div className={styles.reviewComment}>{comment}</div>
                    <div className={styles.editReviewLink} onClick={() => setReviewModalOpen(true)}>
                      {EDIT_REVIEW}
                    </div>
                  </div>
                ) : (
                  <Button
                    className={styles.addReviewBtn}
                    text={ADD_A_TRAIL_REVIEW}
                    onClick={() => setReviewModalOpen(true)}
                    testId="add-a-trail-review-button"
                    variant="default"
                  />
                )
              ) : null}
            </div>
          )}
        </div>
        {data?.trailId && reviewModalOpen && (
          <Suspense fallback={null}>
            <ReviewFormModal
              {...data}
              onReviewAdd={onReviewAdd}
              updateReview={updateReview}
              closeReviewModal={() => setReviewModalOpen(false)}
              activityReview
            />
          </Suspense>
        )}
      </div>
    </CustomProvider>
  );
};

ActionHeader.propTypes = {
  data: object.isRequired,
  isFavorite: bool,
  handleFavoriteClick: func,
  bannerPhotoId: string,
  handleDirectionsClick: func,
  openDownloadModal: func,
  handleShareClick: func,
  saveMapAsClone: func,
  type: string,
  editingEndpoint: bool,
  handleEditEndpointClick: func,
  handleCancelEditEndpointClick: func,
  handleEditClick: func,
  handleDeleteClick: func,
  excludeRatingStructuredData: bool,
  trackTrail: object,
  canEdit: bool
};

ActionHeader.defaultProps = {
  isFavorite: false,
  handleFavoriteClick: () => {},
  bannerPhotoId: null,
  handleDirectionsClick: () => {},
  openDownloadModal: () => {},
  handleShareClick: () => {},
  saveMapAsClone: () => {},
  type: '',
  editingEndpoint: false,
  handleEditEndpointClick: () => {},
  handleCancelEditEndpointClick: () => {},
  handleEditClick: () => {},
  handleDeleteClick: () => {},
  excludeRatingStructuredData: false,
  handleRatingChange: () => {},
  rating: 0
};

export default ActionHeader;
